import { computed, inject, Signal } from '@angular/core';
import { of } from 'rxjs';
import { DropdownData, rxResourceFixed } from '@zayan-erp/shared';
import { DsmProductOption } from '../models/dsm-deal-options.models';
import { DsmPackageProjectModelElevationOption } from '../models/dsm-package.models';
import { DsmApsService } from './dsm-aps.service';
import { DsmModelElevationProductsService } from './dsm-model-elevation-products.service';
import { DsmPackageService } from './dsm-package.service';
import { DsmStateService } from './dsm-state.service';
import { toDsmModelElevationKey } from '../utils/dsm-model-elevation-key.util';

/** Reads as an empty cell rather than a blank one. */
export const DSM_NO_VALUE = '—';

export function toDsmOptionLabel(code: string, description: string): string {
  if (code && description) return `${code} — ${description}`;

  return description || code || DSM_NO_VALUE;
}

/** The ids the cascade is driven by — signals so the resources refetch on change. */
export interface DsmRuleScopeIds {
  projectId: Signal<string | null>;
  modelId: Signal<string | null>;
  elevationId: Signal<string | null>;
}

export interface DsmProjectModelElevationOptions {
  projectOptions: Signal<DropdownData[]>;
  modelOptions: Signal<DropdownData[]>;
  elevationOptions: Signal<DropdownData[]>;
  /** Every model/elevation pair as one option, e.g. "37B1 (A)"; ids from `toDsmModelElevationKey`. */
  modelElevationOptions: Signal<DropdownData[]>;
  isLoadingProjects: Signal<boolean>;
  isLoadingModelElevations: Signal<boolean>;
}

export interface DsmRuleScopeOptions extends DsmProjectModelElevationOptions {
  floorPlanOptions: Signal<DropdownData[]>;
  isLoadingFloorPlans: Signal<boolean>;
}

/**
 * The Project → Model → Elevation dropdown cascade for any DSM screen that picks a home plan.
 * Must be called in an injection context (a field initializer or constructor).
 *
 * Projects come from the sidebar's sales office, and one call per project returns every
 * model/elevation pair (feeding both dropdowns).
 */
export function createDsmProjectModelElevationOptions(ids: DsmRuleScopeIds): DsmProjectModelElevationOptions {
  const apsService = inject(DsmApsService);
  const packageService = inject(DsmPackageService);
  const dsmState = inject(DsmStateService);

  const projectsResource = rxResourceFixed({
    params: () => ({ salesOfficeId: dsmState.selectedSalesOfficeId() }),
    defaultValue: [] as DropdownData[],
    stream: ({ params }) =>
      params.salesOfficeId ? apsService.getSalesOfficeProjects(params.salesOfficeId) : of([] as DropdownData[])
  });

  const modelElevationsResource = rxResourceFixed({
    params: () => ({ projectId: ids.projectId() }),
    defaultValue: [] as DsmPackageProjectModelElevationOption[],
    stream: ({ params }) =>
      params.projectId
        ? packageService.getProjectModelElevations(params.projectId)
        : of([] as DsmPackageProjectModelElevationOption[])
  });

  return {
    projectOptions: computed(() =>
      projectsResource.value().map(({ id, firstLabel, secondLabel }) => ({
        id,
        firstLabel: secondLabel || firstLabel || DSM_NO_VALUE
      }))
    ),
    modelOptions: computed(() => {
      const byId = new Map<string, string>();
      modelElevationsResource.value().forEach(({ modelId, modelName }) => byId.set(modelId, modelName));

      return [...byId].map(([id, firstLabel]) => ({ id, firstLabel: firstLabel || DSM_NO_VALUE }));
    }),
    elevationOptions: computed(() => {
      const modelId = ids.modelId();
      if (!modelId) return [];

      return modelElevationsResource
        .value()
        .filter(pair => pair.modelId === modelId)
        .map(({ elevationId, elevationCode }) => ({ id: elevationId, firstLabel: elevationCode || DSM_NO_VALUE }));
    }),
    // sorted by label, so a model's elevations sit together (the API returns them unordered)
    modelElevationOptions: computed(() =>
      modelElevationsResource
        .value()
        .map(({ modelId, modelName, elevationId, elevationCode }) => ({
          id: toDsmModelElevationKey(modelId, elevationId) as string,
          firstLabel: `${modelName || DSM_NO_VALUE} (${elevationCode || DSM_NO_VALUE})`
        }))
        .sort((a, b) => a.firstLabel.localeCompare(b.firstLabel, undefined, { numeric: true }))
    ),
    isLoadingProjects: computed(() => projectsResource.isLoading()),
    isLoadingModelElevations: computed(() => modelElevationsResource.isLoading())
  };
}

/**
 * The Project → Model → Elevation → Floor Plan dropdown cascade, shared by the
 * Location Rules list and its create/edit form. Must be called in an injection
 * context (a field initializer or constructor).
 *
 * Floor plans are the products of the chosen model + elevation.
 */
export function createDsmRuleScopeOptions(ids: DsmRuleScopeIds): DsmRuleScopeOptions {
  const productsService = inject(DsmModelElevationProductsService);

  /** A floor plan is a product, so it only exists once a model and elevation are picked. */
  const floorPlansResource = rxResourceFixed({
    params: () => ({ modelId: ids.modelId(), elevationId: ids.elevationId() }),
    defaultValue: [] as DsmProductOption[],
    stream: ({ params }) =>
      params.modelId && params.elevationId
        ? productsService.getModelElevationProducts(params.modelId, params.elevationId, undefined, false)
        : of([] as DsmProductOption[])
  });

  return {
    ...createDsmProjectModelElevationOptions(ids),
    floorPlanOptions: computed(() =>
      floorPlansResource
        .value()
        // A product with no id could never be filtered on, so it is not offered.
        .filter(({ productId }) => !!productId)
        .map(({ productId, code, description }) => ({
          id: productId as string,
          firstLabel: toDsmOptionLabel(code, description)
        }))
    ),
    isLoadingFloorPlans: computed(() => floorPlansResource.isLoading())
  };
}
